<template>
  <div class="hris-page">
    <div class="hris-page-inner">
    <div class="hris-page-header">
      <span class="hris-page-eyebrow">Online Forms</span>
      <h1 class="hris-page-title">Change Work Schedule</h1>
      <p class="hris-page-subtitle">Request a change to your work schedule and time zone.</p>
    </div>

    <div class="hris-form-narrow">
    <div class="hris-form-banner hris-form-banner-warning">
      File your Change of Work Schedule one day before the actual date.
    </div>

    <q-form>
      <div class="hris-form-section">
        <h2 class="hris-form-section-title">Inclusive Dates</h2>
        <div class="hris-form-row">
          <div class="hris-form-field">
            <label class="hris-form-label">From</label>
            <q-input v-model="dateFromInput" outlined dense color="green" readonly>
              <template v-slot:append>
                <q-icon name="fa fa-calendar-alt" class="cursor-pointer">
                  <q-popup-proxy ref="qDateFromProxy" transition-show="flip-down" transition-hide="flip-up">
                    <q-date v-model="form.selectedDateFrom" @input="$refs.qDateFromProxy.hide()" today-btn color="green" minimal ></q-date>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <div class="hris-form-field">
            <label class="hris-form-label">To</label>
            <q-input v-model="dateToInput" outlined dense color="green" readonly>
              <template v-slot:append>
                <q-icon name="fa fa-calendar-alt" class="cursor-pointer">
                  <q-popup-proxy ref="qDateToProxy" transition-show="flip-down" transition-hide="flip-up">
                    <q-date v-model="form.selectedDateTo" @input="$refs.qDateToProxy.hide()" today-btn color="green" minimal />
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
        </div>
      </div>

      <div class="hris-form-section">
        <h2 class="hris-form-section-title">Schedule Type</h2>
        <div class="hris-form-row">
          <div class="hris-form-field">
            <label class="hris-form-label">From</label>
            <q-select v-model="form.selectedWorkFrom" :options="workTypeOptions" @input="setOrigTimes(form.selectedWorkFrom)" options-cover outlined dense color="green" />
          </div>
          <div class="hris-form-field">
            <label class="hris-form-label">To</label>
            <q-select v-model="form.selectedWorkTo" :options="workTypeOptions" @input="setNewTimes(form.selectedWorkTo)" options-cover outlined dense color="green" />
          </div>
        </div>
      </div>

      <div class="hris-form-section">
        <h2 class="hris-form-section-title">Inclusive Time</h2>
        <div class="hris-form-row">
          <div class="hris-form-field">
            <label class="hris-form-label">Original Time-In</label>
            <q-input :value="originalTimeIn" :disable="oTimeWorking" outlined dense color="green" readonly>
              <template v-slot:append>
                <q-icon name="fa fa-clock" class="cursor-pointer">
                  <q-popup-proxy transition-show="flip-down" transition-hide="flip-up">
                    <q-time v-model="form.selectedOriginalTimeIn" :minute-options="minuteOptions" landscape color="green" minimal>
                      <div class="row items-center justify-end q-gutter-xs">
                        <q-btn label="Cancel" color="green" style="width: 80px" flat v-close-popup />
                        <q-btn label="OK" color="green" style="width: 80px" flat @click="saveTime('originalTimeIn',form.selectedOriginalTimeIn)" v-close-popup />
                      </div>
                    </q-time>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <div class="hris-form-field">
            <label class="hris-form-label">Original Time-Out</label>
            <q-input v-model="originalTimeOut" :disable="oTimeWorking" outlined dense color="green" readonly />
          </div>
        </div>
        <div class="hris-form-row">
          <div class="hris-form-field">
            <label class="hris-form-label">New Time-In</label>
            <q-input :value="newTimeIn" :disable="nTimeWorking" outlined dense color="green" readonly>
              <template v-slot:append>
                <q-icon name="fa fa-clock" class="cursor-pointer">
                  <q-popup-proxy transition-show="flip-down" transition-hide="flip-up">
                    <q-time v-model="form.selectedNewTimeIn" :minute-options="minuteOptions" landscape color="green" minimal>
                      <div class="row items-center justify-end q-gutter-xs">
                        <q-btn label="Cancel" color="green" style="width: 80px" flat v-close-popup />
                        <q-btn label="OK" color="green" style="width: 80px" flat @click="saveTime('newTimeIn',form.selectedNewTimeIn)" v-close-popup />
                      </div>
                    </q-time>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <div class="hris-form-field">
            <label class="hris-form-label">New Time-Out</label>
            <q-input v-model="newTimeOut" :disable="nTimeWorking" outlined dense color="green" readonly />
          </div>
        </div>
      </div>

      <div class="hris-form-section">
        <h2 class="hris-form-section-title">Time Zone</h2>
        <p class="hris-form-section-hint">Select time zone (Current time zone: {{ currentTimeZone }})</p>
        <div class="hris-form-row">
          <div class="hris-form-field">
            <q-select v-model="form.timeZone" :options="timeZones" options-cover outlined dense color="green" />
          </div>
        </div>
      </div>

      <div class="hris-form-section">
        <h2 class="hris-form-section-title">Reason</h2>
        <div class="hris-form-row">
          <div class="hris-form-field" style="flex-basis: 100%;">
            <q-input type="textarea" v-model="form.cwsReason" outlined dense color="green" maxlength="250" clearable bottom-slots counter hint="Max 250 characters" />
          </div>
        </div>
      </div>

      <div class="hris-form-actions">
        <q-btn unelevated class="hris-q-outline" @click="resetForm" label="Reset" style="min-width: 140px;" />
        <q-btn unelevated class="hris-q-primary" @click="submitApplication" :loading="loading" label="Submit" style="min-width: 140px;">
          <template v-slot:loading>
            <q-spinner color="green" class="on-left" />
            Sending...
          </template>
        </q-btn>
      </div>
    </q-form>
    </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import { date } from 'quasar';
import vueCookies from 'vue-cookies';

let today = Date.now();
let dateToday = date.formatDate(today, 'YYYY/MM/DD');
const scrollToTop = () => {
  const c = document.documentElement.scrollTop || document.body.scrollTop;
  if (c > 0) {
    window.requestAnimationFrame(scrollToTop);
    window.scrollTo(0, c - c / 8);
  }
};

export default {
  name: 'ChangeSchedule',
  props: ['value'],
  data() {
    return {
      shiftHours: 0,
      loading: false,
      date: '',
      dateToday,
      form: {
        selectedDateFrom: '',
        selectedDateTo: '',
        selectedOriginalTimeIn: '',
        selectedOriginalTimeOut: '',
        selectedNewTimeIn: '',
        selectedNewTimeOut: '',
        selectedWorkFrom: '',
        selectedWorkTo: '',
        cwsReason: '',
        timeZone: 'Asia/Manila'
      },
      oTimeWorking: false,
      nTimeWorking: false,
      originalTimeIn: 'Please select a time...',
      originalTimeOut: 'Please select original time-in...',
      newTimeIn: 'Please select a time...',
      newTimeOut: 'Please select new time-in...',
      minuteOptions: [0, 30],
      workTypeOptions: ['New Schedule', 'Restday', 'Duty', 'Vacation Leave','No Schedule'],
      timeZones: ['Asia/Manila', 'Asia/Kolkata', 'Australia/Sydney'],
      currentTimeZone: null,
    }
  },

  created() {
    this.getTimeZone();
  },

  methods: {
    startDate: function(date) {
      return date > this.dateToday;
    },

    endDate: function(date) {
      if (this.form.selectedDateFrom == '') {
        return date > this.dateToday;
      } else {
        return date >= this.form.selectedDateFrom;
      }
    },

    setOrigTimes: function(workType) {
      this.shiftHours = this.userData.shift_hours;
      if (workType == 'Restday') {
        this.oTimeWorking = true;
        this.originalTimeIn = 'Restday';
        this.originalTimeOut = 'Restday';
        this.form.selectedOriginalTimeIn = '00:00';
        this.form.selectedOriginalTimeOut = '00:00';
      } else if (workType == 'Vacation Leave') {
        this.oTimeWorking = true;
        this.originalTimeIn = 'Vacation Leave';
        this.originalTimeOut = 'Vacation Leave';
        this.form.selectedOriginalTimeIn = '00:00';
        this.form.selectedOriginalTimeOut = '00:00';
      } else {
        this.oTimeWorking = false;
        this.originalTimeIn = 'Please select a time...';
        this.originalTimeOut = 'Please select original time-in...';

      }
    },

    setNewTimes: function(workType) {
      if (workType == 'Restday') {
        this.nTimeWorking = true;
        this.newTimeIn = 'Restday';
        this.newTimeOut = 'Restday';
        this.form.selectedNewTimeIn = '00:00';
        this.form.selectedNewTimeOut = '00:00';
      } else if (workType == 'Vacation Leave') {
        this.nTimeWorking = true;
        this.newTimeIn = 'Vacation Leave';
        this.newTimeOut = 'Vacation Leave';
        this.form.selectedNewTimeIn = '00:00';
        this.form.selectedNewTimeOut = '00:00';
      } else {
        this.nTimeWorking = false;
        this.newTimeIn = 'Please select a time...';
        this.newTimeOut = 'Please select new time-in...';
      }
    },

    saveTime: function(type, timeSent) {
      var tempTime = timeSent.split(':');
      var newTime = date.buildDate({ hours: tempTime[0], minutes: tempTime[1] });

      switch (type) {
        case 'originalTimeIn':
          var originalFormattedTimeIn = date.formatDate(newTime, 'hh:mm A');
          this.originalTimeIn = originalFormattedTimeIn;
          var originalTimeOutPlus = date.addToDate(newTime, { hours: this.shiftHours });
          var originalFormattedTimeOut = date.formatDate(originalTimeOutPlus, 'hh:mm A');
          this.originalTimeOut = originalFormattedTimeOut;
          this.form.selectedOriginalTimeOut = date.formatDate(originalTimeOutPlus, 'HH:mm');
          break;
        case 'newTimeIn':
          var newFormattedTimeIn = date.formatDate(newTime, 'hh:mm A');
          this.newTimeIn = newFormattedTimeIn;
          var newTimeOutPlus = date.addToDate(newTime, { hours: this.shiftHours });
          var newFormattedTimeOut = date.formatDate(newTimeOutPlus, 'hh:mm A');
          this.newTimeOut = newFormattedTimeOut;
          this.form.selectedNewTimeOut = date.formatDate(newTimeOutPlus, 'HH:mm');
          break;
      }
    },

    submitApplication: function() {
      if (this.form.selectedDateFrom == undefined || this.form.selectedDateTo == undefined ||
        this.form.selectedOriginalTimeIn == '' || this.form.selectedOriginalTimeOut == '' ||
        this.form.selectedNewTimeIn == '' || this.form.selectedNewTimeOut == '' ||
        this.form.cwsReason == '') {
        this.$q.notify({
          color: 'red-6',
          textColor: 'white',
          icon: 'fa fa-exclamation-circle',
          message: 'Please fill-in all necessary details.'
        });
      } else if (this.form.selectedDateFrom > this.form.selectedDateTo) {
        this.$q.notify({
          color: 'red-6',
          textColor: 'white',
          icon: 'fa fa-exclamation-circle',
          message: 'Please check the date. "Date From" is greater than "Date To".'
        });
      } else {
        this.loading = true;
        this.form.selectedDateFrom = date.formatDate(this.form.selectedDateFrom, 'YYYY-MM-DD');
        this.form.selectedDateTo = date.formatDate(this.form.selectedDateTo, 'YYYY-MM-DD');

        let formData = new FormData();
        formData.append('form_data', JSON.stringify(this.form));

        axios.create({
          baseURL: this.apiUrl,
          headers: {
            'Authorization': `Bearer ${this.token}`
          }
        }).post('/online-forms/applications/cws', formData).then((res) => {
          setTimeout(() => {
            this.loading = false;
            this.$q.notify({
              color: 'green-7',
              textColor: 'white',
              icon: 'fa fa-shipping-fast',
              message: 'Change of Work Schedule Application Submitted'
            });
            this.resetForm();
            // window.location.reload();
          }, 1000);
        });
      }
    },

    // convertTimeZone(timeZone){
    //   this.form.timeZone = timeZone == 'IST' ? 'Asia/Kolkata' 
    //     : timeZone == 'ACT' ? 'Australia/Sydney' 
    //     : 'Asia/Manila';
    //     console.log(this.form.timeZone)
    // },

    resetForm: function() {
      var self = this;
      Object.keys(this.form).forEach(function(key, index) {
        self.form[key] = '';
      });
      this.originalTimeIn = 'Please select a time...';
      this.originalTimeOut = 'Please select original time-in...';
      this.newTimeIn = 'Please select a time...';
      this.newTimeOut = 'Please select new time-in...';
      this.oTimeWorking = false;
      this.nTimeWorking = false;
      setTimeout(() => {
        scrollToTop();
      }, 200);
    },

    async getTimeZone() {
      try {
        axios.create({
            baseURL: this.apiUrl,
            headers: {
              'Authorization': `Bearer ${this.token}`
            }})
            .get('/current-time')
            .then((res) => { this.currentTimeZone = res.data.time_zone;  
              // console.log('response:' + res.data.current_time)
            })
      } catch (error) {
        console.error('Error fetching time:', error);
        this.currentTime = 'Error fetching time'; // Handle error scenario
      }
    }
  },

  computed: {
    dateFromInput: function() {
      if (this.form.selectedDateFrom == '') {
        return 'Please select a date...';
      } else {
        let d = new Date(this.form.selectedDateFrom);
        return date.formatDate(d, 'dddd, MMMM DD, YYYY');
      }
    },

    dateToInput: function() {
      if (this.form.selectedDateTo == '') {
        return 'Please select a date...';
      } else {
        let d = new Date(this.form.selectedDateTo);
        return date.formatDate(d, 'dddd, MMMM DD, YYYY');
      }
    }
  }
}
</script>
<style scoped>
</style>
